import React, { useCallback, useState } from 'react';
import { ImageSourcePropType } from 'react-native';
import { Box } from '@styleseat/ui';
import analytics from '../../../modules/analytics';
import {
  useFeatureFlag,
  useIsMobile,
} from '../../../hooks';
import {
  Stack,
  Touchable,
} from '../../ui';
import Image from '../../shared/Image';
import HeroMobileImage from './images/klarna_learn_more_m.png';
import HeroDesktopImage from './images/klarna_learn_more_d.png';
import { openExternalUrl } from '../../../modules/openExternalUrl';

type ImageInfo = {
  source: ImageSourcePropType;
  width: number;
  height: number;
};

const HeroMobileInfo: ImageInfo = {
  source: HeroMobileImage,
  width: 368,
  height: 191,
};

const HeroDesktopInfo: ImageInfo = {
  source: HeroDesktopImage,
  width: 680,
  height: 197,
};

function openKlarnaPage() {
  analytics.track('klarna_learn_more_banner_clicked');
  openExternalUrl('https://mss.styleseat.com/klarna/');
}

function scaleSizeClamped(imageInfo: ImageInfo, containerWidth: number): ImageInfo {
  const clampFactor = 0.15;
  const rawScale = containerWidth / imageInfo.width;
  const scale = Math.max(Math.min(rawScale, 1 + clampFactor), 1 - clampFactor);
  return {
    source: imageInfo.source,
    width: scale === rawScale ? containerWidth : Math.round(scale * imageInfo.width),
    height: Math.round(scale * imageInfo.height),
  };
}

const KLARNA_HOMEPAGE_BANNER_SWITCH_NAME = 'klarna_learn_more_homepage_banner_ev3986_20220813';

export function useIsKlarnaHomePageHeroEnabled() {
  const {
    loading,
    enabled,
  } = useFeatureFlag(KLARNA_HOMEPAGE_BANNER_SWITCH_NAME);
  return !loading && enabled;
}

export const KlarnaLearnMoreHero: React.FC = () => {
  const isMobile = useIsMobile();
  const heroInfo = isMobile ? HeroMobileInfo : HeroDesktopInfo;
  const [containerWidth, setContainerWidth] = useState<number>(heroInfo.width);

  const imageInfo = isMobile
    ? scaleSizeClamped(heroInfo, containerWidth)
    : heroInfo;

  const handleLayout = useCallback(({ nativeEvent }) => {
    setContainerWidth(nativeEvent.layout.width);
  }, []);

  return (
    <Stack alignItems="center" onLayout={isMobile ? handleLayout : undefined}>
      <Touchable onClick={openKlarnaPage} testID="klarna-learn-more-hero">
        <Box
          borderRadius={8}
          overflow="hidden"
        >
          <Image
            source={imageInfo.source}
            style={{
              width: imageInfo.width,
              height: imageInfo.height,
            }}
            accessibilityLabel="StyleSeat & Klarna. Style Now. Pay Later. Learn More."
          />
        </Box>
      </Touchable>
    </Stack>
  );
};
